import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminProductsApi } from '../api';
import { useTelegram } from '../hooks/useTelegram';
import { Loading } from '../components/Loading';
import type { Product, Category } from '../types';
import './AdminProductEditPage.css';

export const AdminProductEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { tg } = useTelegram();

  const [product, setProduct] = useState<Product | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<number>(0);
  const [price, setPrice] = useState('');
  const [weight, setWeight] = useState('');
  const [pieces, setPieces] = useState('');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [fat, setFat] = useState('');
  const [carbohydrates, setCarbohydrates] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);

  // Image management
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [newImagePreview, setNewImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

   
   
  useEffect(() => {
    // Show back button
    if (tg?.BackButton) {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBack);
    }

    return () => {
      if (tg?.BackButton) {
        tg.BackButton.hide();
        tg.BackButton.offClick(handleBack);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tg]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [productData, categoriesData] = await Promise.all([
        adminProductsApi.getProduct(parseInt(id)),
        adminProductsApi.getCategories(),
      ]);

      setProduct(productData);
      setCategories(categoriesData.categories || []);

      // Fill form with product data
      setName(productData.name);
      setDescription(productData.description);
      setCategoryId(productData.category_id);
      setPrice(productData.price.toString());
      setWeight(productData.weight?.toString() || '');
      setPieces(productData.pieces?.toString() || '');
      setCalories(productData.calories?.toString() || '');
      setProtein(productData.protein?.toString() || '');
      setFat(productData.fat?.toString() || '');
      setCarbohydrates(productData.carbohydrates?.toString() || '');
      setIsAvailable(productData.is_available);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/products');
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !product) return;

    const filesArray = Array.from(files);
    e.target.value = ''; // Reset input immediately

    try {
      setUploadingGallery(true);

      let sortOrder = (product.images?.length || 0) + 1;

      for (const file of filesArray) {
        try {
          // Upload image
          const uploadResponse = await adminProductsApi.uploadImage(product.id, file);

          // Add to gallery
          await adminProductsApi.addProductImage(
            product.id,
            uploadResponse.image_url,
            sortOrder,
            false
          );

          sortOrder++;
        } catch (err) {
          console.error('Error uploading file:', file.name, err);
          tg?.showAlert(`Ошибка загрузки ${file.name}`);
        }
      }

      // Reload product data to show new images
      await loadData();

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success');
      }
    } catch (err: unknown) {
      tg?.showAlert(err instanceof Error ? err.message : 'Ошибка загрузки изображений');
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleDeleteGalleryImage = async (imageId: number) => {
    if (!product) return;

    tg?.showConfirm('Удалить это изображение?', async (confirmed) => {
      if (!confirmed) return;

      try {
        await adminProductsApi.deleteProductImage(product.id, imageId);
        await loadData(); // Reload to get updated gallery
        tg?.showAlert('Изображение удалено');
      } catch (err: unknown) {
        tg?.showAlert(err instanceof Error ? err.message : 'Ошибка удаления изображения');
      }
    });
  };

  const handleSave = async () => {
    if (!product) return;

    // Validation
    if (!name.trim()) {
      tg?.showAlert('Введите название товара');
      return;
    }
    if (!description.trim()) {
      tg?.showAlert('Введите описание товара');
      return;
    }
    if (!categoryId) {
      tg?.showAlert('Выберите категорию');
      return;
    }
    if (!price || parseFloat(price) <= 0) {
      tg?.showAlert('Введите корректную цену');
      return;
    }
    if (!weight && !pieces) {
      tg?.showAlert('Укажите вес или количество штук');
      return;
    }

    try {
      setSaving(true);

      let imageUrl = product.image_url;

      // Upload new main image if selected
      if (newImageFile) {
        setUploadingImage(true);
        const uploadResponse = await adminProductsApi.uploadImage(product.id, newImageFile);
        imageUrl = uploadResponse.image_url;
        setUploadingImage(false);
      }

      // Update product
      await adminProductsApi.updateProduct(product.id, {
        name: name.trim(),
        description: description.trim(),
        category_id: categoryId,
        price: parseFloat(price),
        weight: weight ? parseInt(weight) : null,
        pieces: pieces ? parseInt(pieces) : null,
        calories: calories ? parseFloat(calories) : null,
        protein: protein ? parseFloat(protein) : null,
        fat: fat ? parseFloat(fat) : null,
        carbohydrates: carbohydrates ? parseFloat(carbohydrates) : null,
        is_available: isAvailable,
        image_url: imageUrl,
      });

      tg?.showAlert('Товар успешно обновлен');

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success');
      }

      // Reload product data and clear selected files
      setNewImageFile(null);
      setNewImagePreview(null);
      await loadData();
    } catch (err: unknown) {
      tg?.showAlert(err instanceof Error ? err.message : 'Ошибка сохранения товара');

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  const handleDelete = async () => {
    if (!product) return;

    tg?.showConfirm(
      `Вы уверены, что хотите удалить товар "${product.name}"?`,
      async (confirmed) => {
        if (!confirmed) return;

        try {
          await adminProductsApi.deleteProduct(product.id);
          tg?.showAlert('Товар успешно удален');
          navigate('/admin/products', { replace: true });
        } catch (err: unknown) {
          tg?.showAlert(err instanceof Error ? err.message : 'Ошибка удаления товара');
        }
      }
    );
  };

  if (loading) {
    return (
      <div className="page admin-product-edit-page">
        <div className="admin-product-edit-header">
          <h1 className="admin-product-edit-title">Редактирование товара</h1>
        </div>
        <Loading />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="page admin-product-edit-page">
        <div className="admin-product-edit-header">
          <h1 className="admin-product-edit-title">Редактирование товара</h1>
        </div>
        <div className="admin-product-edit-error">
          <p className="admin-product-edit-error-message">{error || 'Товар не найден'}</p>
          <button className="admin-product-edit-retry-btn" onClick={loadData}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page admin-product-edit-page">
      <div className="admin-product-edit-header">
        <h1 className="admin-product-edit-title">Редактирование товара</h1>
      </div>

      <div className="admin-product-edit-content">
        <div className="admin-product-edit-form">
          {/* Name */}
          <div className="form-group">
            <label className="form-label">Название *</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Введите название товара"
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Описание *</label>
            <textarea
              className="form-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Введите описание товара"
              rows={4}
            />
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">Категория *</label>
            <select
              className="form-select"
              value={categoryId}
              onChange={(e) => setCategoryId(parseInt(e.target.value))}
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Price */}
          <div className="form-group">
            <label className="form-label">Цена (₽) *</label>
            <input
              type="number"
              className="form-input"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0"
              min="0"
              step="0.01"
            />
          </div>

          {/* Weight and Pieces */}
          <span className="form-hint">Укажите вес или количество штук (или оба параметра)</span>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Вес (г)</label>
              <input
                type="number"
                className="form-input"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="0"
                min="0"
                step="1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Количество штук</label>
              <input
                type="number"
                className="form-input"
                value={pieces}
                onChange={(e) => setPieces(e.target.value)}
                placeholder="0"
                min="0"
                step="1"
              />
            </div>
          </div>

          {/* Nutritional Info */}
          <div className="form-section-title">Пищевая ценность (на 100г)</div>

          <div className="form-group">
            <label className="form-label">Калории (ккал)</label>
            <input
              type="number"
              className="form-input"
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
              placeholder="0"
              min="0"
              step="0.1"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Белки (г)</label>
              <input
                type="number"
                className="form-input"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
                placeholder="0"
                min="0"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Жиры (г)</label>
              <input
                type="number"
                className="form-input"
                value={fat}
                onChange={(e) => setFat(e.target.value)}
                placeholder="0"
                min="0"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Углеводы (г)</label>
              <input
                type="number"
                className="form-input"
                value={carbohydrates}
                onChange={(e) => setCarbohydrates(e.target.value)}
                placeholder="0"
                min="0"
                step="0.1"
              />
            </div>
          </div>

          {/* Availability */}
          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={isAvailable}
                onChange={(e) => setIsAvailable(e.target.checked)}
              />
              <span className="checkbox-label">Доступен для заказа</span>
            </label>
          </div>

          {/* Main Image */}
          <div className="form-group">
            <label className="form-label">Основное изображение</label>
            <div className="image-upload-section">
              <div className="main-image-container">
                {newImagePreview ? (
                  <div className="image-preview main">
                    <img src={newImagePreview} alt="Preview" />
                    <button
                      className="image-remove-btn"
                      onClick={() => {
                        setNewImageFile(null);
                        setNewImagePreview(null);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : product.image_url ? (
                  <div className="image-preview main">
                    <img src={product.image_url} alt={product.name} />
                  </div>
                ) : (
                  <label className="image-upload-placeholder">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    <span>Загрузить изображение</span>
                  </label>
                )}
                {(newImagePreview || product.image_url) && (
                  <label className="image-change-btn">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Изменить
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Gallery */}
          <div className="form-group">
            <label className="form-label">Галерея изображений</label>
            <div className="gallery-section">
              <div className="gallery-grid">
                {product.images && product.images.length > 0 && product.images.map((image) => (
                  <div key={image.id} className="gallery-item">
                    <img src={image.image_url} alt="" />
                    <div className="gallery-item-actions">
                      <button
                        className="gallery-action-btn delete"
                        onClick={() => handleDeleteGalleryImage(image.id)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}

                <label className={`gallery-add-item ${uploadingGallery ? 'uploading' : ''}`}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryImageSelect}
                    style={{ display: 'none' }}
                    disabled={uploadingGallery}
                  />
                  {uploadingGallery ? (
                    <>
                      <svg className="spinner" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                      </svg>
                      <span>Загрузка...</span>
                    </>
                  ) : (
                    <>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      <span>Добавить фото</span>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            className="admin-product-edit-save-btn"
            onClick={handleSave}
            disabled={saving || uploadingImage}
          >
            {saving || uploadingImage ? 'Сохранение...' : 'Сохранить изменения'}
          </button>

          {/* Delete Button */}
          <button className="admin-product-edit-delete-btn" onClick={handleDelete}>
            Удалить товар
          </button>
        </div>
      </div>
    </div>
  );
};
